import { MoneyValueDto } from '@/types/MoneyValueDto.ts';
import { Currency } from '@/features/dashboard/paymentConfig/types/Currency.ts';

/**
 * Arytmetyka kwot, której backend wyręczyć nie może.
 *
 * Zostały trzy zastosowania. Pierwsze to **zestawienia wymyślone przez ekran**:
 * suma sekcji i suma wiersza zbierającego kilka pozycji księgi — backend nie
 * zna ani sekcji, ani „jednego wiersza na opcję”, więc te sumy nie mają skąd
 * przyjść. Drugie to **przesunięcie stanu o deltę** (`wartość + wpływ paczki`),
 * gdzie obie liczby są policzone przez backend. Trzecie to **fikstury testów**,
 * które muszą policzyć podgląd paczki dla kanonicznego rachunku
 * (`__fixtures__/mockBillDelta.ts`).
 *
 * Bilansu paczki, delt osób ani delt wierszy tu **nie liczymy** — te
 * przychodzą z `GET …/changes/{changeId}/preview`.
 *
 * **Rezerwacja bywa wielowalutowa i nic tego nie zwija.** Kwoty w różnych
 * walutach nigdy się nie dodają, więc każde zestawienie jest listą po jednej
 * pozycji na walutę (`sumMoney`, `addMoneyLists`); kursów nie ma i nie będzie.
 * Operacje na dwóch pojedynczych kwotach (`addMoney`, `subtractMoney`) zostają
 * jako budulec tamtych i rzucają przy niezgodnej walucie — na tym poziomie
 * różnica walut jest pomyłką wołającego, a nie stanem rezerwacji.
 *
 * Wynik jest zawsze zaokrąglony do dwóch miejsc, żeby dodawanie liczb
 * zmiennoprzecinkowych nie zostawiało ogonów typu `0.30000000000000004`.
 */

/** Zaokrąglenie do groszy. */
export const roundAmount = (amount: number): number =>
  Math.round((amount + Number.EPSILON) * 100) / 100;

export const money = (amount: number, currency: Currency): MoneyValueDto => ({
  amount: roundAmount(amount),
  currency,
});

export const zeroMoney = (currency: Currency): MoneyValueDto =>
  money(0, currency);

const assertSameCurrency = (a: MoneyValueDto, b: MoneyValueDto): void => {
  if (a.currency !== b.currency) {
    throw new Error(
      `Nie można łączyć kwot w różnych walutach: ${a.currency} i ${b.currency}`,
    );
  }
};

export const addMoney = (a: MoneyValueDto, b: MoneyValueDto): MoneyValueDto => {
  assertSameCurrency(a, b);
  return money(a.amount + b.amount, a.currency);
};

export const subtractMoney = (
  a: MoneyValueDto,
  b: MoneyValueDto,
): MoneyValueDto => {
  assertSameCurrency(a, b);
  return money(a.amount - b.amount, a.currency);
};

export const isZeroMoney = (value: MoneyValueDto): boolean =>
  roundAmount(value.amount) === 0;

/**
 * Suma kwot **pogrupowana po walucie** — po jednej pozycji na walutę, w
 * kolejności pierwszego wystąpienia.
 *
 * Waluty nie są tu filtrem ani błędem: rezerwacja bywa wielowalutowa (200 zł
 * i 333 €), a suma takich pozycji to po prostu dwie liczby obok siebie.
 */
export const sumMoney = (values: MoneyValueDto[]): MoneyValueDto[] => {
  const byCurrency = new Map<Currency, MoneyValueDto>();

  values.forEach((value) => {
    const current = byCurrency.get(value.currency);
    byCurrency.set(
      value.currency,
      current ? addMoney(current, value) : money(value.amount, value.currency),
    );
  });

  return [...byCurrency.values()];
};

/** Suma kwot z listy obiektów, z których każdy niesie własne zestawienie. */
export const sumAmounts = (
  items: { amount?: MoneyValueDto[] | null }[],
): MoneyValueDto[] => sumMoney(items.flatMap((item) => item.amount ?? []));

/**
 * Dodanie dwóch zestawień po walutach — „stan przed” przesunięty o deltę.
 * Waluta obecna tylko po jednej stronie przechodzi nietknięta.
 */
export const addMoneyLists = (
  a: MoneyValueDto[],
  b: MoneyValueDto[],
): MoneyValueDto[] => sumMoney([...a, ...b]);

/** Zestawienie z odwróconym znakiem — zwrot pokazany jako kwota ujemna. */
export const negateMoneyList = (values: MoneyValueDto[]): MoneyValueDto[] =>
  values.map((value) => money(-value.amount, value.currency));

/** Zestawienie bez skutku finansowego: puste albo złożone z samych zer. */
export const isZeroMoneyList = (values: MoneyValueDto[]): boolean =>
  values.every(isZeroMoney);

/**
 * Znak zestawienia: `1` dopłata, `-1` zwrot, `0` brak wpływu.
 *
 * Zestawienie mieszane (dopłata w złotych, zwrot w euro) znaku nie ma i dostaje
 * `null` — napisy takie jak „dopłata” muszą wtedy powiedzieć coś innego,
 * zamiast wybierać jedną z walut na chybił trafił.
 */
export const moneyListDirection = (
  values: MoneyValueDto[],
): 1 | -1 | 0 | null => {
  const nonZero = values.filter((value) => !isZeroMoney(value));
  if (nonZero.length === 0) {
    return 0;
  }
  if (nonZero.every((value) => value.amount > 0)) {
    return 1;
  }
  if (nonZero.every((value) => value.amount < 0)) {
    return -1;
  }
  return null;
};

/** Wartości bezwzględne — kwota bez znaku, gdy kierunek niesie sam napis. */
export const absMoneyList = (values: MoneyValueDto[]): MoneyValueDto[] =>
  values.map((value) => money(Math.abs(value.amount), value.currency));
